import DATA from "@util/data-element";
import {getElement, parseSelector} from "@util/get-element";
import {executeAfterTransition} from "@util/transition";
import Backdrop from "@component/backdrop";

const DATA_KEY = "modal";
const ESCAPE_KEY = "Escape";

const CLASS_NAME_OPEN = "modal-open";
const CLASS_NAME_ANIMATE = "fade";
const CLASS_NAME_SHOW = "show";

const SELECTOR_DATA_TOGGLE = 'a[href^="#modal-"], button[data-toggle^="#modal-"]';

const SELECTOR_MODAL_DIALOG = ".modal-dialog";
const SELECTOR_MODAL_BODY = ".modal-body";
const SELECTOR_MODAL_CLOSE = '[data-dismiss="modal"]';
const OPEN_SELECTOR = ".modal.show";

class Modal {
	private readonly _element: HTMLElement;
	private readonly _dialog: HTMLElement;
	private readonly _dismiss: NodeListOf<HTMLElement>;
	private _backdrop: Backdrop;
	private _isVisible: boolean;
	private _isTransitioning: boolean;
	
	constructor(element = null) {
		element = getElement(element);
		
		if (!element) {
			return;
		}
		
		this._element = element;
		this._dialog = this._element.querySelector(SELECTOR_MODAL_DIALOG);
		this._dismiss = this._element.querySelectorAll(SELECTOR_MODAL_CLOSE);
		this._backdrop = this._initializeBackDrop();
		this._isVisible = false;
		this._isTransitioning = false;
		this._addEventListeners();
		DATA.set(this._element, DATA_KEY, this);
	}
	
	static getInstance(element) {
		return DATA.get(getElement(element), DATA_KEY);
	}
	
	static getOrCreateInstance(element) {
		return this.getInstance(element) || new Modal(element);
	}
	
	toggle() {
		return this._isVisible ? this.hide() : this.show();
	}
	
	show() {
		if (this._isVisible || this._isTransitioning) {
			return;
		}
	
		this._isVisible = true;
		this._isTransitioning = true;
		
		document.body.classList.add(CLASS_NAME_OPEN);
		
		this._element.dispatchEvent(new CustomEvent('toggleModal', {detail: 'show'}));
		
		this._backdrop.show(() => this._showElement());
	}
	
	hide() {
		if (!this._isVisible || this._isTransitioning) {
			return;
		}
		
		this._isVisible = false;
		this._isTransitioning = true;
		
		this._element.classList.remove(CLASS_NAME_SHOW);
		
		this._queueCallback(() => this._hideModal(), this._element, this._isAnimated());
	}
	
	_queueCallback(callback, element, isAnimated = true) {
		executeAfterTransition(callback, element, isAnimated);
	}
	
	_addEventListeners() {
		this._element.addEventListener("keydown", (ev) => {
			if (ev.key !== ESCAPE_KEY) {
				return;
			}
			
			this.hide();
		});
		
		if (this._dismiss.length > 0) {
			this._dismiss.forEach((element) => {
				element.addEventListener("click", (ev) => {
					if (["A"].includes((ev.target as HTMLElement).tagName)) {
						ev.preventDefault();
					}
					
					this.hide();
				});
			});
		}
		
		this._element.addEventListener("mousedown", (ev) => {
			this._element.addEventListener(
				"click",
				(ev2) => {
					if (this._element !== ev.target || this._element !== ev2.target) {
						return;
					}
					
					this.hide();
				},
				{
					once: true,
				},
			);
		});
	}
	
	_showElement() {
		if (!document.body.contains(this._element)) {
			document.body.append(this._element);
		}
		
		this._element.style.display = "block";
		this._element.removeAttribute("aria-hidden");
		this._element.setAttribute("aria-modal", String(true));
		this._element.setAttribute("role", "dialog");
		this._element.setAttribute("tabindex", "1");
		this._element.scrollTop = 0;
		
		const modalBody = this._dialog.querySelector(SELECTOR_MODAL_BODY);
		if (modalBody) {
			modalBody.scrollTop = 0;
		}
		
		this._element.classList.add(CLASS_NAME_SHOW);
		
		const transitionComplete = () => {
			this._element.focus();
			this._isTransitioning = false;
		};
		
		this._queueCallback(transitionComplete, this._dialog, this._isAnimated());
	}
	
	_hideModal() {
		this._element.style.display = "none";
		this._element.setAttribute("aria-hidden", String(true));
		this._element.removeAttribute("aria-modal");
		this._element.removeAttribute("role");
		this._element.removeAttribute("tabindex");
		this._isTransitioning = false;
		
		this._element.dispatchEvent(new CustomEvent('toggleModal', {detail: 'hide'}));
		
		this._backdrop.hide(() => {
			const openModals = document.querySelectorAll(OPEN_SELECTOR);
			if (openModals.length === 0) {
				document.body.classList.remove(CLASS_NAME_OPEN);
			}
		});
	}
	
	_isAnimated() {
		return this._element.classList.contains(CLASS_NAME_ANIMATE);
	}
	
	_initializeBackDrop() {
		return new Backdrop({
			isVisible: true,
		});
	}
}

document.addEventListener("click", (ev) => {
	let target = ev.target as HTMLElement;
	
	if (!target) {
		return;
	}
	
	const delegateTarget = target.closest(SELECTOR_DATA_TOGGLE) as HTMLElement;
	
	if (!delegateTarget) {
		return;
	}
	
	target = delegateTarget;    
	
	if (target) {
		ev.preventDefault();
	}
	
	let hrefAttribute = target.getAttribute("href");
	const dataAttribute = target.getAttribute("data-toggle");

	if (dataAttribute && dataAttribute.startsWith("#")) {
		hrefAttribute = dataAttribute;
	}
	
	if (hrefAttribute.includes("#") && !hrefAttribute.startsWith("#")) {
		hrefAttribute = `#${hrefAttribute.split("#")[1]}`;
	}
	
	const selector = hrefAttribute && hrefAttribute !== "#" ? hrefAttribute.trim() : null;
	
	if (!selector) {
		return;
	}
	
	const modalTarget = document.querySelector(parseSelector(selector));
	
	const alreadyOpen = document.querySelector(OPEN_SELECTOR);
	if (alreadyOpen) {
		Modal.getInstance(alreadyOpen).hide();
	}

	const data = Modal.getOrCreateInstance(modalTarget);

	data.toggle();
});

export default Modal;
